import type {BillingMetricKey} from "../billingUtils";
import {Button, Stack, ToggleButton, ToggleButtonGroup} from "@mui/material";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import TollRoundedIcon from "@mui/icons-material/TollRounded";
import MonetizationOnRoundedIcon from "@mui/icons-material/MonetizationOnRounded";
import {alpha, styled} from "@mui/material/styles";
import {useTranslation} from "react-i18next";

const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({theme}) => ({
    backgroundColor:
        theme.palette.mode === "light"
            ? alpha(theme.palette.primary.main, 0.06)
            : alpha(theme.palette.primary.light, 0.08),
    padding: theme.spacing(0.5),
    borderRadius: 12,
    border: `1px solid ${alpha(theme.palette.text.primary, theme.palette.mode === "light" ? 0.12 : 0.18)}`,
    "& .MuiToggleButton-root": {
        textTransform: "none",
        fontWeight: 700,
        border: "1px solid transparent",
        borderRadius: 9,
        color: theme.palette.text.primary,
        gap: theme.spacing(0.75),
        paddingInline: theme.spacing(1.5),
        opacity: 0.88,
        transition: "background-color 140ms ease, border-color 140ms ease, color 140ms ease, opacity 140ms ease",
        "&.Mui-selected": {
            backgroundColor:
                theme.palette.mode === "light"
                    ? theme.palette.background.paper
                    : alpha(theme.palette.primary.main, 0.16),
            color: theme.palette.primary.main,
            borderColor: alpha(theme.palette.primary.main, theme.palette.mode === "light" ? 0.2 : 0.36),
            boxShadow:
                theme.palette.mode === "light"
                    ? "0 2px 6px rgba(0,0,0,0.1)"
                    : `inset 0 0 0 1px ${alpha(theme.palette.primary.light, 0.12)}`,
            opacity: 1,
            "&:hover": {
                backgroundColor:
                    theme.palette.mode === "light"
                        ? theme.palette.background.paper
                        : alpha(theme.palette.primary.main, 0.2),
            },
        },
        "&:hover": {
            backgroundColor:
                theme.palette.mode === "light"
                    ? alpha(theme.palette.background.paper, 0.78)
                    : alpha(theme.palette.background.paper, 0.28),
            borderColor: alpha(theme.palette.text.primary, theme.palette.mode === "light" ? 0.12 : 0.22),
            color: theme.palette.text.primary,
            opacity: 1,
        },
    },
}));

type BarChartButtonGroupProps = {
    onDownloadBillingCsv: () => Promise<void>;
    canDownloadBillingCsv: boolean;
    isDownloadPending: boolean;
    selectedMetric: BillingMetricKey;
    onSelectedMetricChange: (metric: BillingMetricKey) => void;
    isBillingLoading: boolean;
    hasValidDateRange: boolean;
};

export const BarChartButtonGroup = ({
    onDownloadBillingCsv,
    canDownloadBillingCsv,
    isDownloadPending,
    selectedMetric,
    onSelectedMetricChange,
    isBillingLoading,
    hasValidDateRange,
}: BarChartButtonGroupProps) => {
    const {t: translate} = useTranslation();

    return (
        <Stack direction={{xs: "column", sm: "row"}} spacing={1.5} alignItems={{xs: "stretch", sm: "center"}}>
            <Button
                variant="outlined"
                startIcon={<DownloadRoundedIcon />}
                onClick={() => void onDownloadBillingCsv()}
                disabled={!canDownloadBillingCsv || isDownloadPending}
                sx={{fontWeight: 700, minWidth: 180}}
            >
                {isDownloadPending
                    ? translate("admin.billing.exportCsv.loading")
                    : translate("admin.billing.exportCsv.label")}
            </Button>
            <StyledToggleButtonGroup
                value={selectedMetric}
                exclusive
                size="small"
                onChange={(_event, value: BillingMetricKey | null) => {
                    if (value) {
                        onSelectedMetricChange(value);
                    }
                }}
                disabled={isBillingLoading || !hasValidDateRange}
            >
                <ToggleButton value="tokens">
                    <TollRoundedIcon sx={{fontSize: 18}} />
                    {translate("admin.billing.metrics.tokens")}
                </ToggleButton>
                <ToggleButton value="price">
                    <MonetizationOnRoundedIcon sx={{fontSize: 18}} />
                    {translate("admin.billing.metrics.price")}
                </ToggleButton>
            </StyledToggleButtonGroup>
        </Stack>
    );
};
